import { useQuery } from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';
import { SCOPE_PERMISSION_WRITE } from 'business/constants';
import { AccessibleValues, PermissionType } from 'business/definitions';
import {
    FINANCE_SCOPE,
    GESTIONNAIRE_TABLE,
    ORG_CHART_FINANCE_TABLE,
} from 'routes/BudgetModule/config/constants';
import { transformUrlParams } from 'helpers/urlHelpers';
import { snakify } from 'helpers/syntaxHelper';
import { createBudgetQueryKeys } from '../createBudgetQueryKeys';

export type EditableValuesByResource = {
    gestionnaire: string[];
    orgChartFinance: string[];
};

const fetchAccessibleValues = async (
    fieldName: string,
    scopeName: string,
    permission: PermissionType,
): Promise<AccessibleValues> => {
    const params = new URLSearchParams({
        field_name: fieldName,
        scope_name: scopeName,
        permission: permission,
    });
    const response = await RQAPICallWithData<AccessibleValues>({
        endpoint: `auth/scopes/accessible_values?${params.toString()}`,
        method: 'GET',
        preProcessingUrlCallback: (url) =>
            transformUrlParams(
                url,
                ['field_name', 'scope_name', 'permission'],
                snakify,
            ),
    });

    return response.content.data;
};

/**
 * Custom hook to retrieve editable values by budget resource ('gestionnaire' and 'orgChartFinance').
 */
export const useGetEditableValuesByBudgetResource = (): {
    data: EditableValuesByResource;
    isLoading: boolean;
    isError: boolean;
} => {
    const { writeScopesQueryKeys } = createBudgetQueryKeys();
    const gestionnaireQuery = useQuery({
        queryKey: writeScopesQueryKeys(GESTIONNAIRE_TABLE),
        queryFn: () =>
            fetchAccessibleValues(
                GESTIONNAIRE_TABLE,
                FINANCE_SCOPE,
                SCOPE_PERMISSION_WRITE,
            ),
    });
    const orgChartFinanceQuery = useQuery({
        queryKey: writeScopesQueryKeys(ORG_CHART_FINANCE_TABLE),
        queryFn: () =>
            fetchAccessibleValues(
                ORG_CHART_FINANCE_TABLE,
                FINANCE_SCOPE,
                SCOPE_PERMISSION_WRITE,
            ),
    });

    const isLoading =
        gestionnaireQuery.isLoading || orgChartFinanceQuery.isLoading;
    const isError = gestionnaireQuery.isError || orgChartFinanceQuery.isError;

    const defaultValue = {
        gestionnaire: [],
        orgChartFinance: [],
    };
    const data =
        isLoading || isError
            ? defaultValue
            : {
                  gestionnaire: gestionnaireQuery.data?.values || [],
                  orgChartFinance: orgChartFinanceQuery.data?.values || [],
              };

    return {
        data,
        isLoading,
        isError,
    };
};
